import { useState } from 'react';
import OpenAI from "openai";

import Alert from '../component/alert';
import ImageCard from '../component/ImageCard';
import AppIcon from '../component/AppIcon';

const API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

const openai = new OpenAI({apiKey: API_KEY, dangerouslyAllowBrowser: true});

const ImageGenerator = () => {
    const [prompt, setPrompt] = useState('');
    const [images, setImages] = useState([]);
    const [loading, isLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertType, setAlertType] = useState('warning');
    const [alertMessage, setAlertMessage] = useState('');
    const [size, setSize] = useState('1024x1024');
    
    const generateImages = async () => {
        if (!prompt.trim()) {
            setAlertType('warning');
            setAlertMessage('Please enter a prompt before generating images.');
            setShowAlert(true);
            return;
        }

        isLoading(true);
        setShowAlert(false);
        
        try {
            const response = await openai.images.generate({
                model: "dall-e-3",
                prompt: prompt,
                size: size,
            });

            setImages(response.data);
            
            if (response.data.length > 0) {
                setAlertType('success');
                setAlertMessage('Images generated successfully!');
                setShowAlert(true);
            }

        } catch (error) {
            if (error.response) {
                console.log(error.response.status);
                console.log(error.response.data);
            } else {
                console.log('Error:', error.message);
            }
            
            setAlertType('error');
            setAlertMessage('An error occurred while generating images. Your prompt may contain content that is not allowed by our safety system.');
            setShowAlert(true);
        } finally {
            isLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            generateImages();
        }
    };

    return (
        <div className="max-w-5xl mx-auto animate-fadeIn">
            <div className="card dark:bg-gray-800 mb-8">
                <div className="mb-6 flex items-center">
                    <AppIcon className="h-12 w-12 mr-4" />
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">AI Image Generator</h1>
                        <p className="text-gray-600 dark:text-gray-300">Create stunning images with DALL-E 3 AI</p>
                    </div>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Describe the image you want to create
                        </label>
                        <textarea
                            id="prompt"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="A futuristic cityscape with flying cars and neon lights..."
                            className="input min-h-[100px] resize-y"
                            rows="3"
                        />
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full sm:w-1/3">
                            <label htmlFor="size" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Image Size
                            </label>
                            <select
                                id="size"
                                value={size}
                                onChange={(e) => setSize(e.target.value)}
                                className="input"
                            >
                                <option value="1024x1024">Square (1024×1024)</option>
                                <option value="1792x1024">Landscape (1792×1024)</option>
                                <option value="1024x1792">Portrait (1024×1792)</option>
                            </select>
                        </div>
                        
                        <div className="w-full sm:w-2/3 flex items-end">
                            <button 
                                onClick={generateImages} 
                                className="btn btn-primary w-full flex items-center justify-center gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                    </svg>
                                )}
                                Generate Image
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            {showAlert && <Alert message={alertMessage} type={alertType} />}
            
            
            
            {images.length > 0 && (
                <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Generated Images</h2>
                    <div className="grid grid-cols-1 gap-6">
                        {images.map((image, index) => (
                            <ImageCard key={index} image={image} index={index} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageGenerator;